import { useState } from 'react';
import { Link } from 'wouter';
import {
  Archive,
  Check,
  GitMerge,
  Lightbulb,
  Pencil,
  RotateCcw,
  ShieldOff,
  Wand2,
  X,
} from 'lucide-react';
import { AdminAPI } from '@shared/types/api';
import { useToast } from '@/components/ux/Toast';
import {
  useClassifyTriageTicket,
  useMergeTriageTickets,
  useReclassifyTriageTicket,
  useSetTriageStatus,
  useTriageRemember,
  useTriageTicketDetail,
  useTriageTickets,
} from '../../api/useAdminTriage';
import { timeAgo } from '../ops/opsUi';
import TriageConversation from './TriageConversation';
import TriageResolutions from './TriageResolutions';
import TriageUserCard from './TriageUserCard';
import { CategoryChip, DispositionChip, PriorityMark, TicketStatusChip } from './triageUi';

// Full ticket view: classification header, suggested-merge banner, the
// interleaved conversation, the active draft, the teach box, and the user
// card. Fetches its own detail (polling) so deep links work standalone.

const DISPOSITIONS: AdminAPI.TriageDisposition[] = [
  'auto_answerable',
  'needs_review',
  'needs_action',
  'spam',
];

function errMessage(err: unknown, fallback: string): string {
  return err instanceof Error ? err.message : fallback;
}

// ---------------------------------------------------------------------------
// Classification panel: disposition / priority selects apply immediately,
// category and title save explicitly.
// ---------------------------------------------------------------------------

function ClassificationPanel({ ticket }: { ticket: AdminAPI.TriageTicketSummary }) {
  const toast = useToast();
  const classify = useClassifyTriageTicket();
  const [category, setCategory] = useState(ticket.category ?? '');

  const apply = (fields: AdminAPI.UpdateTriageTicketRequest) => {
    classify.mutate(
      { ticketId: ticket.id, ...fields },
      {
        onSuccess: () => toast.success('Classification updated'),
        onError: (err) => toast.error(errMessage(err, 'Update failed')),
      }
    );
  };

  return (
    <div className="flex flex-wrap items-center gap-2">
      <select
        value={ticket.disposition ?? ''}
        onChange={(e) => apply({ disposition: e.target.value as AdminAPI.TriageDisposition })}
        disabled={classify.isPending}
        className="px-2 py-1 rounded-lg border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-900 text-xs"
      >
        <option value="" disabled>
          disposition…
        </option>
        {DISPOSITIONS.map((d) => (
          <option key={d} value={d}>
            {d.replace(/_/g, ' ')}
          </option>
        ))}
      </select>
      <select
        value={ticket.priority}
        onChange={(e) => apply({ priority: e.target.value as 'low' | 'normal' | 'high' })}
        disabled={classify.isPending}
        className="px-2 py-1 rounded-lg border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-900 text-xs"
      >
        <option value="low">low</option>
        <option value="normal">normal</option>
        <option value="high">high</option>
      </select>
      <input
        value={category}
        onChange={(e) => setCategory(e.target.value)}
        placeholder="category"
        className="w-32 px-2 py-1 rounded-lg border border-neutral-300 dark:border-neutral-600 bg-transparent text-xs"
      />
      {category.trim() !== (ticket.category ?? '') && (
        <button
          onClick={() => apply({ category: category.trim() })}
          disabled={classify.isPending || !category.trim()}
          className="px-2 py-1 rounded-lg bg-primary-500 text-white text-xs font-medium hover:bg-primary-600 disabled:opacity-50"
        >
          Save
        </button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Merge picker: choose open tickets to fold INTO this one.
// ---------------------------------------------------------------------------

function MergePicker({
  ticket,
  onClose,
}: {
  ticket: AdminAPI.TriageTicketSummary;
  onClose: () => void;
}) {
  const toast = useToast();
  const merge = useMergeTriageTickets();
  const { data, isLoading } = useTriageTickets({});
  const [filter, setFilter] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const all = data?.pages.flatMap((p) => p.data?.tickets ?? []) ?? [];
  const needle = filter.trim().toLowerCase();
  const candidates = all.filter((t) => {
    if (t.id === ticket.id) return false;
    if (!needle) return true;
    return [t.title, t.summary, t.counterpartName, t.counterpartEmail]
      .some((field) => field?.toLowerCase().includes(needle));
  });

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const doMerge = () => {
    merge.mutate(
      { targetTicketId: ticket.id, sourceTicketIds: [...selected] },
      {
        onSuccess: (res) => {
          toast.success(`Merged ${res.data?.mergedCount ?? selected.size} ticket(s) into this one`);
          onClose();
        },
        onError: (err) => toast.error(errMessage(err, 'Merge failed')),
      }
    );
  };

  return (
    <div className="rounded-xl border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 p-3 space-y-2">
      <div className="flex items-center gap-2">
        <GitMerge className="w-4 h-4 text-dark-muted" />
        <h3 className="text-sm font-semibold flex-1">Merge other tickets into this one</h3>
        <button onClick={onClose} className="text-dark-muted hover:text-primary-500">
          <X className="w-4 h-4" />
        </button>
      </div>
      <input
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
        placeholder="Filter open tickets by title or sender"
        className="w-full px-3 py-1.5 rounded-lg border border-neutral-300 dark:border-neutral-600 bg-transparent text-xs"
      />
      <div className="max-h-56 overflow-y-auto divide-y divide-neutral-100 dark:divide-neutral-800">
        {isLoading && <p className="text-xs text-dark-muted py-1">Loading…</p>}
        {!isLoading && candidates.length === 0 && (
          <p className="text-xs text-dark-muted py-1">No matching open tickets.</p>
        )}
        {candidates.map((t) => (
          <label key={t.id} className="flex items-center gap-2 py-1.5 cursor-pointer">
            <input
              type="checkbox"
              checked={selected.has(t.id)}
              onChange={() => toggle(t.id)}
              className="rounded"
            />
            <span className="text-xs truncate flex-1">
              {t.title ?? t.summary ?? '(untitled)'}
              <span className="text-dark-muted">
                {' '}
                · {t.counterpartName ?? t.counterpartEmail ?? 'unknown sender'}
              </span>
            </span>
            <span className="text-[11px] text-dark-muted whitespace-nowrap">
              {timeAgo(t.lastInboundAt ?? t.createdAt)}
            </span>
          </label>
        ))}
      </div>
      <button
        onClick={doMerge}
        disabled={selected.size === 0 || merge.isPending}
        className="px-3 py-1.5 rounded-lg bg-primary-500 text-white text-xs font-medium hover:bg-primary-600 disabled:opacity-50"
      >
        {merge.isPending ? 'Merging…' : `Merge ${selected.size || ''} selected`}
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// "Remember this" teach box.
// ---------------------------------------------------------------------------

function RememberBox({ ticketId }: { ticketId: string }) {
  const toast = useToast();
  const remember = useTriageRemember();
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');

  const save = () => {
    remember.mutate(
      { ticketId, text: text.trim() },
      {
        onSuccess: () => {
          toast.success('Filed as a pending playbook rule');
          setText('');
          setOpen(false);
        },
        onError: (err) => toast.error(errMessage(err, 'Could not save')),
      }
    );
  };

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1 text-[11px] text-dark-muted hover:text-primary-500"
      >
        <Lightbulb className="w-3 h-3" />
        Teach the agent a rule
      </button>
    );
  }

  return (
    <div className="flex gap-2 items-center">
      <Lightbulb className="w-3.5 h-3.5 text-amber-500 shrink-0" />
      <input
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && text.trim().length >= 3) save();
          if (e.key === 'Escape') setOpen(false);
        }}
        autoFocus
        placeholder="A rule for next time (filed for review on the Playbook page)"
        className="flex-1 px-3 py-1.5 rounded-lg border border-neutral-300 dark:border-neutral-600 bg-transparent text-xs"
      />
      <button
        onClick={save}
        disabled={remember.isPending || text.trim().length < 3}
        className="px-3 py-1.5 rounded-lg border border-neutral-300 dark:border-neutral-600 text-xs font-medium hover:border-primary-400 hover:text-primary-500 disabled:opacity-50"
      >
        Save
      </button>
      <button onClick={() => setOpen(false)} className="text-dark-muted hover:text-primary-500">
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main detail
// ---------------------------------------------------------------------------

export default function TriageTicketDetail({ ticketId }: { ticketId: string }) {
  const toast = useToast();
  const { data, isLoading } = useTriageTicketDetail(ticketId);
  const setStatus = useSetTriageStatus();
  const reclassify = useReclassifyTriageTicket();
  const classify = useClassifyTriageTicket();
  const mergeTickets = useMergeTriageTickets();

  const [dismissedMerges, setDismissedMerges] = useState<Set<string>>(new Set());
  const [mergePickerOpen, setMergePickerOpen] = useState(false);
  const [editingTitle, setEditingTitle] = useState(false);
  const [editingClassification, setEditingClassification] = useState(false);
  const [titleInput, setTitleInput] = useState('');

  const detail = data?.data;
  if (isLoading || !detail) {
    return <p className="text-sm text-dark-muted p-4">{isLoading ? 'Loading…' : 'Ticket not found.'}</p>;
  }

  const {
    ticket,
    threads,
    messages,
    drafts,
    matchCandidates,
    matchedUser,
    history,
    suggestedMerges,
    similar,
    accountContext,
  } = detail;
  // Options of the latest proposal, in the order the agent ranked them.
  const activeDrafts = drafts
    .filter((d) => d.status === 'proposed' || d.status === 'edited')
    .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
  const visibleMerges = suggestedMerges.filter((m) => !dismissedMerges.has(m.id));
  const isClosed = ticket.status === 'resolved' || ticket.status === 'spam';

  const changeStatus = (status: AdminAPI.SetTriageStatusRequest['status'], label: string) => {
    setStatus.mutate(
      { ticketId: ticket.id, status },
      {
        onSuccess: () => toast.success(label),
        onError: (err) => toast.error(errMessage(err, 'Status change failed')),
      }
    );
  };

  const saveTitle = () => {
    const next = titleInput.trim();
    setEditingTitle(false);
    if (!next || next === (ticket.title ?? '')) return;
    classify.mutate(
      { ticketId: ticket.id, title: next },
      {
        onSuccess: () => toast.success('Title updated'),
        onError: (err) => toast.error(errMessage(err, 'Title update failed')),
      }
    );
  };

  const mergeSuggestion = (sourceId: string) => {
    mergeTickets.mutate(
      { targetTicketId: ticket.id, sourceTicketIds: [sourceId] },
      {
        onSuccess: () => toast.success('Tickets merged'),
        onError: (err) => toast.error(errMessage(err, 'Merge failed')),
      }
    );
  };

  return (
    <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_18rem] gap-4">
      <div className="min-w-0 space-y-3">
        {/* Header */}
        <div className="rounded-xl border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 p-3 space-y-2.5">
          <div className="flex items-start gap-2">
            {editingTitle ? (
              <input
                value={titleInput}
                onChange={(e) => setTitleInput(e.target.value)}
                onBlur={saveTitle}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') saveTitle();
                  if (e.key === 'Escape') setEditingTitle(false);
                }}
                autoFocus
                className="flex-1 px-2 py-1 rounded-lg border border-neutral-300 dark:border-neutral-600 bg-transparent text-base font-semibold"
              />
            ) : (
              <h2 className="text-base font-semibold flex-1 min-w-0 break-words">
                {ticket.title ?? ticket.summary ?? '(untitled ticket)'}
                <button
                  onClick={() => {
                    setTitleInput(ticket.title ?? '');
                    setEditingTitle(true);
                  }}
                  title="Edit title"
                  className="ml-1.5 inline-flex align-middle text-dark-muted hover:text-primary-500"
                >
                  <Pencil className="w-3.5 h-3.5" />
                </button>
              </h2>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <TicketStatusChip status={ticket.status} />
            <DispositionChip disposition={ticket.disposition} />
            <CategoryChip category={ticket.category} />
            <PriorityMark priority={ticket.priority} />
            <button
              onClick={() => setEditingClassification(!editingClassification)}
              title="Edit classification"
              className={`text-dark-muted hover:text-primary-500 ${editingClassification ? 'text-primary-500' : ''}`}
            >
              <Pencil className="w-3 h-3" />
            </button>
            <span className="text-xs text-dark-muted">
              {ticket.counterpartName ?? (ticket.counterpartEmail ? null : 'unknown sender')}
              {ticket.counterpartName && ticket.counterpartEmail && ' · '}
              {ticket.counterpartEmail && (
                <a
                  href={`mailto:${ticket.counterpartEmail}`}
                  className="hover:text-primary-500 select-all"
                >
                  {ticket.counterpartEmail}
                </a>
              )}
              {' '}· last inbound{' '}
              {timeAgo(ticket.lastInboundAt)}
            </span>
          </div>

          {ticket.summary && ticket.title && (
            <p className="text-xs text-dark-muted">{ticket.summary}</p>
          )}

          {/* Keyed so the category input resyncs when the server-side value changes
              (e.g. after a reclassify run lands). */}
          {editingClassification && (
            <ClassificationPanel key={`${ticket.id}:${ticket.category ?? ''}`} ticket={ticket} />
          )}

          <div className="flex flex-wrap items-center gap-2 pt-1 border-t border-neutral-100 dark:border-neutral-800">
            {ticket.status !== 'merged' && (
              <>
                {!isClosed && (
                  <>
                    <button
                      onClick={() => changeStatus('resolved', 'Ticket resolved')}
                      disabled={setStatus.isPending}
                      className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-emerald-600 text-white text-xs font-medium hover:bg-emerald-700 disabled:opacity-50"
                    >
                      <Check className="w-3.5 h-3.5" />
                      Resolve
                    </button>
                    <button
                      onClick={() => changeStatus('spam', 'Marked as spam')}
                      disabled={setStatus.isPending}
                      className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-neutral-300 dark:border-neutral-600 text-xs font-medium text-dark-muted hover:border-red-400 hover:text-red-500 disabled:opacity-50"
                    >
                      <ShieldOff className="w-3.5 h-3.5" />
                      Spam
                    </button>
                  </>
                )}
                {isClosed && (
                  <button
                    onClick={() => changeStatus('classified', 'Ticket reopened')}
                    disabled={setStatus.isPending}
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-neutral-300 dark:border-neutral-600 text-xs font-medium hover:border-primary-400 hover:text-primary-500 disabled:opacity-50"
                  >
                    <RotateCcw className="w-3.5 h-3.5" />
                    Reopen
                  </button>
                )}
              </>
            )}
            <button
              onClick={() =>
                reclassify.mutate(ticket.id, {
                  onSuccess: (res) =>
                    toast.success(`Reclassify run queued (${res.data?.outcome ?? 'queued'})`),
                  onError: (err) => toast.error(errMessage(err, 'Reclassify failed')),
                })
              }
              disabled={reclassify.isPending}
              title="Enqueue a fresh classification run for this ticket"
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-neutral-300 dark:border-neutral-600 text-xs font-medium text-dark-muted hover:border-primary-400 hover:text-primary-500 disabled:opacity-50"
            >
              <Wand2 className="w-3.5 h-3.5" />
              Reclassify
            </button>
            <button
              onClick={() => setMergePickerOpen(!mergePickerOpen)}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-neutral-300 dark:border-neutral-600 text-xs font-medium text-dark-muted hover:border-primary-400 hover:text-primary-500"
            >
              <GitMerge className="w-3.5 h-3.5" />
              Merge into…
            </button>
          </div>
        </div>

        {/* Suggested merges banner */}
        {visibleMerges.length > 0 && (
          <div className="rounded-xl border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 p-3 space-y-1.5">
            {visibleMerges.map((suggestion) => (
              <div key={suggestion.id} className="flex items-center gap-2 flex-wrap">
                <Archive className="w-3.5 h-3.5 text-amber-600 shrink-0" />
                <span className="text-xs flex-1 min-w-0">
                  Looks like the same issue as{' '}
                  <Link
                    href={`/triage/${suggestion.id}`}
                    className="font-medium text-primary-500 hover:underline"
                  >
                    {suggestion.title ?? suggestion.counterpartEmail ?? suggestion.id.slice(0, 8)}
                  </Link>
                  {suggestion.lastInboundAt && (
                    <span className="text-dark-muted"> ({timeAgo(suggestion.lastInboundAt)})</span>
                  )}
                </span>
                <button
                  onClick={() => mergeSuggestion(suggestion.id)}
                  disabled={mergeTickets.isPending}
                  className="px-2.5 py-1 rounded-lg bg-amber-600 text-white text-xs font-medium hover:bg-amber-700 disabled:opacity-50"
                >
                  Merge
                </button>
                <button
                  onClick={() =>
                    setDismissedMerges((prev) => new Set(prev).add(suggestion.id))
                  }
                  className="text-dark-muted hover:text-primary-500"
                  title="Hide this suggestion"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}

        {mergePickerOpen && (
          <MergePicker ticket={ticket} onClose={() => setMergePickerOpen(false)} />
        )}

        {/* Conversation */}
        <div className="rounded-xl border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 p-3">
          <TriageConversation threads={threads} messages={messages} />
        </div>

        {ticket.status !== 'merged' && (
          <TriageResolutions
            key={ticket.id}
            ticketId={ticket.id}
            drafts={activeDrafts}
          threads={threads}
            account={accountContext}
            userLinked={!!ticket.matchedUserId}
          />
        )}

        <RememberBox ticketId={ticket.id} />

        {/* Similar tickets strip */}
        {similar.length > 0 && (
          <p className="text-[11px] text-dark-muted">
            Similar:{' '}
            {similar.map((s, i) => (
              <span key={s.id}>
                {i > 0 && ' · '}
                <Link href={`/triage/${s.id}`} className="text-primary-500 hover:underline">
                  {s.title ?? s.category ?? s.id.slice(0, 8)}
                </Link>
              </span>
            ))}
          </p>
        )}
      </div>

      {/* Right column */}
      <div className="min-w-0">
        <TriageUserCard
          ticket={ticket}
          matchedUser={matchedUser}
          matchCandidates={matchCandidates}
          history={history}
        />
      </div>
    </div>
  );
}
